import { useTranslation } from 'react-i18next';
import { HelpCircle, LogOut, Settings } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * The portal's avatar (W5, B3-ClientDashboard, quick win h4). A click used to
 * log the client out at once; it now opens the account menu, as everywhere
 * else: who is signed in, Paramètres, Aide, then « Se déconnecter » last.
 */
interface Props {
  name?: string | null;
  email?: string | null;
  onNavigate: (tab: 'settings' | 'help') => void;
  onLogout: () => void;
}

export function initials(name?: string | null, email?: string | null): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  const [first, second] = words;
  if (first && second) return (first.charAt(0) + second.charAt(0)).toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  return (email ?? '').slice(0, 2).toUpperCase() || '?';
}

export default function PortalAccountMenu({ name, email, onNavigate, onLogout }: Props) {
  const { t } = useTranslation(['pages']);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('pages:portal.account.open')}
          title={t('pages:portal.account.open')}
          data-testid="portal-account-menu"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gray-900 text-sm font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
        >
          {initials(name, email)}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        {(name || email) && (
          <DropdownMenuLabel className="font-normal">
            {name && <p className="truncate text-sm font-medium text-gray-900">{name}</p>}
            {email && <p className="truncate text-xs text-gray-500">{email}</p>}
          </DropdownMenuLabel>
        )}
        {(name || email) && <DropdownMenuSeparator />}
        <DropdownMenuItem onSelect={() => onNavigate('settings')}>
          <Settings className="mr-2 h-4 w-4" aria-hidden />
          {t('pages:portal.sections.settings')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onNavigate('help')}>
          <HelpCircle className="mr-2 h-4 w-4" aria-hidden />
          {t('pages:portal.sections.help')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onLogout} className="text-red-600">
          <LogOut className="mr-2 h-4 w-4" aria-hidden />
          {t('pages:portal.dashboard.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
